import axios from 'axios'
import { errorHandler, getUrl } from '@/api/utils/http-helpers'

const START = 'START'
const END = 'END'
const ERROR = 'ERROR'
const SET_FILTERS = 'SET_FILTERS'
const SET_PAGE = 'SET_PAGE'
const SET_EXPORTING = 'SET_EXPORTING'
const SET_RETENTION_POLICY = 'SET_RETENTION_POLICY'
const SET_RETENTION_LOADING = 'SET_RETENTION_LOADING'
const SET_RETENTION_SAVING = 'SET_RETENTION_SAVING'

const createState = () => ({
  items: [],
  totalCount: 0,
  filters: {
    from: '',
    to: '',
    user: '',
    profile: '',
    action: '',
    limit: 50,
    offset: 0
  },
  isLoading: false,
  isLoaded: false,
  isExporting: false,
  retentionPolicy: null,
  isRetentionPolicyLoading: false,
  isRetentionPolicySaving: false,
  error: false
})

const getters = {
  auditTrail (state) {
    return state.items
  },
  auditTrailTotalCount (state) {
    return state.totalCount
  },
  auditTrailFilters (state) {
    return state.filters
  },
  isAuditTrailLoading (state) {
    return state.isLoading
  },
  isAuditTrailExporting (state) {
    return state.isExporting
  },
  auditRetentionPolicy (state) {
    return state.retentionPolicy
  },
  isAuditRetentionPolicyLoading (state) {
    return state.isRetentionPolicyLoading
  },
  isAuditRetentionPolicySaving (state) {
    return state.isRetentionPolicySaving
  },
  auditTrailError (state) {
    return state.error
  },
  totalCount (state) {
    return state.totalCount
  },
  filters (state) {
    return state.filters
  },
  isLoading (state) {
    return state.isLoading
  },
  isExporting (state) {
    return state.isExporting
  }
}

const buildParams = (filters) => {
  const params = {
    limit: filters.limit,
    offset: filters.offset
  }
  if (filters.from) {
    params.from = filters.from
  }
  if (filters.to) {
    params.to = filters.to
  }
  if (filters.user) {
    params.user = filters.user
  }
  if (filters.profile) {
    params.profile = filters.profile
  }
  if (filters.action) {
    params.action = filters.action
  }
  return params
}

const actions = {
  async loadAuditTrail ({ commit, state }, payload = {}) {
    const nextFilters = Object.assign({}, state.filters, payload)
    commit('SET_FILTERS', nextFilters)
    commit('START')
    try {
      const response = await axios.get(getUrl('/audit-trail'), {
        params: buildParams(nextFilters),
        withCredentials: true
      })
      commit('SET_PAGE', response.data)
      return response.data
    } catch (error) {
      errorHandler(error)
      commit('ERROR')
      throw error
    } finally {
      commit('END')
    }
  },

  async exportAuditTrail ({ commit, state }, payload) {
    const format = payload && payload.format ? payload.format : 'csv'
    const filters = Object.assign({}, state.filters, payload && payload.filters ? payload.filters : {})
    commit('SET_EXPORTING', true)
    try {
      const response = await axios.get(getUrl('/audit-trail/export'), {
        params: Object.assign({ format }, buildParams(filters)),
        responseType: 'blob',
        withCredentials: true
      })
      return response
    } catch (error) {
      errorHandler(error)
      commit('ERROR')
      throw error
    } finally {
      commit('SET_EXPORTING', false)
    }
  },

  async loadAuditRetentionPolicy ({ commit }) {
    commit('SET_RETENTION_LOADING', true)
    try {
      const response = await axios.get(getUrl('/audit-trail/retention-policy'), {
        withCredentials: true
      })
      commit('SET_RETENTION_POLICY', response.data)
      return response.data
    } catch (error) {
      errorHandler(error)
      commit('ERROR')
      throw error
    } finally {
      commit('SET_RETENTION_LOADING', false)
    }
  },

  async updateAuditRetentionPolicy ({ commit }, payload) {
    commit('SET_RETENTION_SAVING', true)
    try {
      const response = await axios.put(getUrl('/audit-trail/retention-policy'), payload, {
        withCredentials: true
      })
      commit('SET_RETENTION_POLICY', response.data)
      return response.data
    } catch (error) {
      errorHandler(error)
      commit('ERROR')
      throw error
    } finally {
      commit('SET_RETENTION_SAVING', false)
    }
  }
}

const mutations = {
  [START] (state) {
    state.isLoading = true
    state.isLoaded = false
    state.error = false
  },

  [END] (state) {
    state.isLoading = false
    state.isLoaded = true
  },

  [ERROR] (state) {
    state.error = true
  },

  [SET_FILTERS] (state, payload) {
    state.filters = Object.assign({}, state.filters, payload)
  },

  [SET_PAGE] (state, payload) {
    state.items = payload && payload.items ? payload.items : []
    state.totalCount = payload && payload.totalCount ? payload.totalCount : 0
    if (payload && payload.limit != null) {
      state.filters.limit = payload.limit
    }
    if (payload && payload.offset != null) {
      state.filters.offset = payload.offset
    }
  },

  [SET_EXPORTING] (state, payload) {
    state.isExporting = payload
  },

  [SET_RETENTION_POLICY] (state, payload) {
    state.retentionPolicy = payload ? Object.assign({}, payload) : null
  },

  [SET_RETENTION_LOADING] (state, payload) {
    state.isRetentionPolicyLoading = payload
  },

  [SET_RETENTION_SAVING] (state, payload) {
    state.isRetentionPolicySaving = payload
  }
}

export default {
  namespaced: true,
  state: createState,
  getters,
  actions,
  mutations
}
